import React, { useEffect, useRef, useState } from 'react';
import './OpenCaseRoulette.css';
import { CaseInfo } from '../../../../../models/response/CaseInfo';
import { useStore } from '../../../../../store/store';
import { translations } from '../../../../../configs/translations';
import { Prize } from '../../../../../models/Prize';
import { ModuleWindow } from '../../../../../components/moduleWindow/ModuleWindow';
import { formatTextWithParams } from '../../../../../utils/formatTextWithParams';

interface OpenCaseRouletteProps {
  caseInfo: CaseInfo;
  prize: Prize;
  playAnim: boolean;
  setPlayAnim: (play: boolean) => void; 
  handleEsc: () => void;
  handleTakePrize: () => void;
  handleDiscardPrize: () => void;
  isModuleWindowOpen: boolean;
  openAlert: () => void;
  setIsModuleWindowOpen: (open: boolean) => void;
}

export const OpenCaseRoulette = ({ caseInfo, prize, playAnim, setPlayAnim, handleEsc, handleTakePrize, handleDiscardPrize, isModuleWindowOpen, openAlert, setIsModuleWindowOpen }: OpenCaseRouletteProps) => {
  const PRIZE_WIDTH = 120;
  const CONTAINER_WIDTH = 1080;
  const CENTER_POSITION = CONTAINER_WIDTH / 2;

  const RANDOM_SPINS = 65; // длина рандомного списка

  const language = useStore(state => state.language) as keyof typeof translations;
  const t = translations[language];

  const getSpinPrizes = () => {
    const result = [];
    const basePrizes = caseInfo.prizes;
    for (let i = 0; i < RANDOM_SPINS - 5; i++) {
      const randomPrize = basePrizes[Math.floor(Math.random() * basePrizes.length)];
      result.push(randomPrize);
    }
    result.push(prize); // выигрышный приз
    for (let i = 0; i < 4; i++) {
      const randomPrize = basePrizes[Math.floor(Math.random() * basePrizes.length)];
      result.push(randomPrize);
    }
    return result;
  };

  useEffect(() => {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        handleEsc();
      }
    });

    return () => {
      document.removeEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          handleEsc();
        }
      });
    };
  }, []);

  const [spinPrizes, setSpinPrizes] = useState(getSpinPrizes());
  const [offset, setOffset] = useState(0);

  // refs для анимации
  const offsetRef = useRef(0);
  const startTimeRef = useRef<number | null>(null);
  const animationFrameId = useRef<number | null>(null);

  useEffect(() => {
    if (playAnim) {
      setSpinPrizes(getSpinPrizes());
      offsetRef.current = 0;
      setOffset(0);
      startTimeRef.current = null;
    }
  }, [playAnim]);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const lastTickIndexRef = useRef<number>(-1);

  useEffect(() => {
    audioRef.current = new Audio('/sounds/tick.mp3');
    audioRef.current.volume = 0.05;
  }, []);

  useEffect(() => {
    if (!playAnim) {
        if (animationFrameId.current) {
          cancelAnimationFrame(animationFrameId.current);
          animationFrameId.current = null;
        }
        return;
    }
  
    const duration = 15000;
    const totalLength = spinPrizes.length * PRIZE_WIDTH;
    const finalIndex = spinPrizes.length - 5; // последний приз — тот, на котором останавливаемся

    const winSound = new Audio('/sounds/win.mp3');
    winSound.volume = 0.1;

    const animate = (timestamp: number) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const elapsed = timestamp - startTimeRef.current;
  
      const easeOutQuad = (t: number) => t * (2 - t);
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutQuad(progress);
  
      const targetOffset = (finalIndex * PRIZE_WIDTH) - CENTER_POSITION + (PRIZE_WIDTH / 2);
      const currentOffset = easedProgress * targetOffset;
  
      if (progress < 1) {
        offsetRef.current = currentOffset % totalLength;
      } else {
        offsetRef.current = targetOffset;
      }
  
      setOffset(offsetRef.current);
  
      // Звук перелистывания
      const currentTickIndex = Math.floor(offsetRef.current / PRIZE_WIDTH);
      if (currentTickIndex !== lastTickIndexRef.current) {
        lastTickIndexRef.current = currentTickIndex;
        if (audioRef.current) {
          audioRef.current.currentTime = 0;
          audioRef.current.play().catch(() => {});
        }
      }
  
      if (progress < 1) {
        animationFrameId.current = requestAnimationFrame(animate);
      } else {
        winSound.play().catch(() => {});
        setPlayAnim(false);
      }
    };
  
    animationFrameId.current = requestAnimationFrame(animate);
  
    return () => {
      if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current);
      animationFrameId.current = null;
      startTimeRef.current = null;
    };
  }, [playAnim, spinPrizes, setPlayAnim]);

  return (
    <div className="roulette-wrapper">
      <div className='roulette-container' style={{ visibility: 'hidden' }} />
      <div className="roulette-container" style={{ width: `${CONTAINER_WIDTH}px` }}>
        <div
          className="roulette-prizes"
          style={{ transform: `translateX(${-offset}px)` }}
        >
          {spinPrizes.map((p, i) => (
            <div className="roulette-prize" key={i}>
              <img src={`./cases/prizes/${p.name}.png`} alt={p.name} />
              <div className="roulette-prize-name">{p.name}</div>
            </div>
          ))}
        </div>
        <div className="roulette-pointer" />
      </div>

      {!playAnim ? (
        <div className='roulette-footer-container'>
          <div>
            <p className='roulette-case-you-won'>{t.you_won}</p>
            <p className='roulette-footer-prize-title'>{prize.name}</p>
          </div>

          <div className='roulette-footer-buttons'>

             <div className="donate-shop-card-button-container">
                <div 
                    onClick={handleTakePrize} 
                    className="donate-shop-card-button"
                >
                    <p>{t.take}</p>
                </div>
              </div>

              <div className="donate-shop-card-button-container donate-shop-card-button-container-discard">
                <div 
                    onClick={openAlert} 
                    className="donate-shop-card-button donate-shop-card-button-discard"
                >
                    <p>{t.discard}</p>
                    <div className='donate-shop-card-button-discard-icon'>
                        <img src="./icons/32px/scrap.png" alt="scrap" />
                        <p>{prize.discardCost}</p>
                    </div>
                </div>
              </div>
          </div>
        </div>
      ) : <div className='roulette-container' style={{ visibility: 'hidden' }} />}
      {isModuleWindowOpen && <ModuleWindow
        title={t.discard_prize}
        text={formatTextWithParams(t.discard_prize_text, [prize?.name || '', prize?.discardCost || ''])}
        onYes={() => handleDiscardPrize()}
        onNo={() => setIsModuleWindowOpen(false)}
      />}
    </div>
  );
};
